import { useEffect, useState } from "react";
import { Button, Form, Input, Radio, Space, Spin, message } from "antd";
import type { RadioChangeEvent } from "antd";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
  getSubmitReviewApi,
  submitReviewApi,
} from "../../services/review.service";
import { FaRegSmile } from "react-icons/fa";

const satisfiedQ = "How satisfied are you with your personalized space?";
const improveServiceQ = "How can we improve this service for you?";
const mainBenefitQ =
  "What is the main benefit you received from Missing Skincare?";
const peopleThinkQ =
  "What type of people do you think would most benefit from Missing Skincare?";

const LikedYourSpace = () => {
  const profile: any = useAppSelector((state) => state?.profile);
  const [submitting, setSubmitting] = useState<any>(false);
  const [feedbacks, setFeedbacks] = useState<any>([]);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [disableInput, setDisableInput] = useState(false);
  const [showSelectedQuestion, setShowSelectedQuestion] =
    useState<boolean>(false);
  const [showSelectedValue, setshowSelectedValue] = useState<boolean>(false);
  const [showSuccessScreen, setSuccessScreen] = useState<boolean>(false);

  const firstName = profile?.name?.split(" ")[0];

  const handleFormSubmit: any = async (values: any) => {
    try {
      let feedbackData: any = {
        [satisfiedQ]: values?.satisfied,
      };
      if (values?.improveService) {
        feedbackData = {
          ...feedbackData,
          [improveServiceQ]: values?.improveService,
        };
      }
      if (values?.mainBenefit) {
        feedbackData = {
          ...feedbackData,
          [mainBenefitQ]: values?.mainBenefit,
        };
      }
      if (values?.peopleThink) {
        feedbackData = {
          ...feedbackData,
          [peopleThinkQ]: values?.peopleThink,
        };
      }
      // return
      setSubmitting(true);

      ReactGA.event({
        category: "Store",
        action: "Liked Your Space",
        label: "Rating submitted in liked your space",
        nonInteraction: true,
        transport: "xhr",
      });

      await dispatch(
        submitReviewApi({
          data: { feedback: feedbackData },
          failureCallback: () => {},
          successCallback: (data: any) => {
            setShowSelectedQuestion(true);
            const numberOfProperties = Object?.keys(
              data?.data?.feedback
            )?.length;
            if (numberOfProperties >= 2) {
              setSuccessScreen(true);
            }
            setshowSelectedValue(numberOfProperties <= 1);
            fetchReviewData();
          },
          finalCallback: () => {
            setSubmitting(false);
          },
        })
      );
    } catch (error) {
      message.error("Failed to submit form. Please try again.");
    }
  };

  const onChange = (e: RadioChangeEvent) => {
    setValue(e.target.value);
    setShowSelectedQuestion(true);
    if (showSelectedValue) return;
    handleFormSubmit(form.getFieldsValue());
  };

  useEffect(() => {
    fetchReviewData();
    setSuccessScreen(false);
  }, []);

  const fetchReviewData = () => {
    setSubmitting(true);
    dispatch(
      getSubmitReviewApi({
        failureCallback: () => {
          message.error("Something went wrong while fetching feedback data");
        },
        successCallback: (data?: any) => {
          setFeedbacks(data?.feedback);
          form.setFieldValue("satisfied", data?.feedback[satisfiedQ]);
          form.setFieldValue("improveService", data?.feedback[improveServiceQ]);
          form.setFieldValue("mainBenefit", data?.feedback[mainBenefitQ]);
          form.setFieldValue("peopleThink", data?.feedback[peopleThinkQ]);
          let isDisabled =
            data?.feedback[improveServiceQ] ||
            data?.feedback[mainBenefitQ] ||
            data?.feedback[peopleThinkQ];
          setDisableInput(isDisabled);
        },
        finalCallback: () => {
          setSubmitting(false);
        },
      })
    );
  };

  return (
    <>
      <Spin spinning={submitting}>
        <div className="likedYourSpacePage">
          <div className="content__holder">
            {firstName ? <h3>Let’s Be Honest, {firstName}</h3> : <Spin />}
          </div>
          {showSuccessScreen ? (
            <div className="feedback">
              <div className="form__holder success-screen">
                <FaRegSmile />
                Your feedback has been received. Thank you!
              </div>
            </div>
          ) : (
            <Form layout="vertical" form={form} onFinish={handleFormSubmit}>
              <div className="honest__options">
                <h3>{satisfiedQ}</h3>
                <Form.Item
                  name="satisfied"
                  rules={[
                    {
                      required: true,
                      message: "You have to select alteast one option",
                    },
                  ]}
                >
                  <Radio.Group
                    onChange={onChange}
                    value={value}
                    disabled={disableInput}
                  >
                    <Space direction="vertical">
                      <Radio value="Very Satisfied">Very Satisfied</Radio>
                      <Radio value="Somewhat Satisfied">
                        Somewhat Satisfied
                      </Radio>
                      <Radio value="Not Satisfied">Not Satisfied</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </div>
              {showSelectedQuestion ||
              (feedbacks && Object.keys(feedbacks).length >= 2) ? (
                <div className="feedback">
                  <div className="form__holder">
                    <Form.Item label={improveServiceQ} name="improveService">
                      <Input readOnly={disableInput} />
                    </Form.Item>
                  </div>
                  <div className="form__holder">
                    <Form.Item label={mainBenefitQ} name="mainBenefit">
                      <Input readOnly={disableInput} />
                    </Form.Item>
                  </div>
                  <div className="form__holder">
                    <Form.Item label={peopleThinkQ} name="peopleThink">
                      <Input readOnly={disableInput} />
                    </Form.Item>
                  </div>
                  <div className="footer-cta">
                    <Button
                      className="btn-primary cta-btn"
                      htmlType="submit"
                      disabled={submitting || disableInput}
                      loading={submitting}
                    >
                      {feedbacks && Object.keys(feedbacks).length >= 2
                        ? "Already submitted"
                        : "Submit"}
                    </Button>
                  </div>
                </div>
              ) : null}
            </Form>
          )}
        </div>
        <div className="footer-cta">
          <Button
            className="btn-primary cta-btn"
            onClick={() => {
              navigate("/store/appreciate-you");
              ReactGA.event({
                category: "Store",
                action: `Go to The Appreciate You button clicked`,
                label: "",
                nonInteraction: true, // optional, true/false
                transport: "xhr",
              });
            }}
          >
         Go to Appreciate You
          </Button>
        </div>
      </Spin>
    </>
  );
};

export default LikedYourSpace;
